import React from 'react'
import { Avatar, SvgIcon, Box, useTheme, Typography } from '@mui/material'
import { AdminPropertyAd } from 'types'
import { ReactComponent as BedroomSVG } from 'assets/svg/icons/estate_bedroom4.svg'
import { ReactComponent as EtosSVG } from 'assets/svg/icons/etos_kataskevis.svg'
import { ReactComponent as FloorSVG } from 'assets/svg/icons/floor-icon4.svg'
import { ReactComponent as BathroomsSvg } from 'assets/svg/icons/bathrooms.svg'
import { ReactComponent as HeatSvg } from 'assets/svg/icons/heat.svg'
import { ReactComponent as EnergyClassSVG } from 'assets/svg/new-icons/energyclass.svg'
import { ReactComponent as ListingTypeSVG } from 'assets/svg/new-icons/listingtype.svg'
import { ReactComponent as FurnishedSVG } from 'assets/svg/new-icons/furnished.svg'
import { ReactComponent as FireplaceSVG } from 'assets/svg/new-icons/fireplace.svg'
import { ReactComponent as ListingIdSVG } from 'assets/svg/new-icons/listingId.svg'
import { ReactComponent as SpacesSVG } from 'assets/svg/new-icons/spaces.svg'
import { ReactComponent as RenovationSVG } from 'assets/svg/new-icons/renovation.svg'
import { ReactComponent as ParkingSVG } from 'assets/svg/new-icons/parking.svg'
import { ReactComponent as ElevatorSVG } from 'assets/svg/new-icons/elevator.svg'
import { ReactComponent as InOutPlanSVG } from 'assets/svg/new-icons/inoutplan.svg'
import { ReactComponent as StructureFactorSVG } from 'assets/svg/new-icons/structurefactor.svg'
import { ReactComponent as UsageSVG } from 'assets/svg/new-icons/usage.svg'
import { ReactComponent as PricePerSquareMeterSVG } from 'assets/svg/new-icons/pricepersquaremeter.svg'
import { ReactComponent as WithBuildingSVG } from 'assets/svg/new-icons/withbuilding.svg'
import { ReactComponent as CompensationSVG } from 'assets/svg/new-icons/compensation.svg'
import { ReactComponent as DimensionsSVG } from 'assets/svg/new-icons/dimensions.svg'
import { ReactComponent as AccessSVG } from 'assets/svg/new-icons/access.svg'
import { ReactComponent as CoverageFactorSVG } from 'assets/svg/new-icons/coveragefactor.svg'
import {
  GetBathroomsWcs,
  GetCoverageFactor,
  GetBedrooms,
  GetBuildFactor,
  GetConstructionYear,
  GetEnergyClass,
  GetElevator,
  GetHeat,
  GetPlans,
  GetPricePerBuildableArea,
  GetPropertyAdExternalId,
  GetRenovationYear,
  GetSpaces,
  GetUsage,
  ParseFloors,
  GetFurnished,
  GetCommercialParking,
  GetFireplaces,
  GetEstateType,
  GetCompensation,
  GetWithBuilding,
  GetAccess,
  GetDimensions,
} from './helpers'

interface Props {
  propertyAd: AdminPropertyAd
  isExpanded: boolean
  matchesSM: boolean
  matchesTablet: boolean
  matchesLaptop: boolean
}

function ListingIcons(props: Props) {
  const { propertyAd, isExpanded, matchesSM, matchesTablet } = props

  const theme = useTheme()

  const icons = {
    estate: [
      {
        icon: (
          <SvgIcon
            component={BedroomSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '17.5px', sm: '34px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetBedrooms propertyAd={propertyAd} isExpanded />,
      },
      {
        icon: (
          <SvgIcon
            component={EtosSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetConstructionYear
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={FloorSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '17px', sm: '29px' },
              height: 'auto',
            }}
          />
        ),
        getter: <ParseFloors propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={BathroomsSvg}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetBathroomsWcs propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={HeatSvg}
            inheritViewBox
            sx={{
              width: { xs: '20.5px', sm: '32.5px' },
              height: 'auto',
              color: 'black',
            }}
          />
        ),
        getter: <GetHeat propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={EnergyClassSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetEnergyClass propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={ListingTypeSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { sx: '20px', sm: '36px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetEstateType propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={FurnishedSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '36px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetFurnished propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={FireplaceSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '34px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetFireplaces propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={ListingIdSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '16px', sm: '26px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetPropertyAdExternalId
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
    ],
    commercial: [
      {
        icon: (
          <SvgIcon
            component={SpacesSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetSpaces propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={EtosSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetConstructionYear
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={RenovationSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetRenovationYear propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={FloorSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '17px', sm: '29px' },
              height: 'auto',
            }}
          />
        ),
        getter: <ParseFloors propertyAd={propertyAd} isExpanded={isExpanded} />,
      },

      {
        icon: (
          <SvgIcon
            component={HeatSvg}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20.5px', sm: '32.5px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetHeat propertyAd={propertyAd} isExpanded={isExpanded} />,
      },

      {
        icon: (
          <SvgIcon
            component={EnergyClassSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetEnergyClass propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={ParkingSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '21px', sm: '33px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetCommercialParking
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={ElevatorSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetElevator propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={BathroomsSvg}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetBathroomsWcs propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },

      {
        icon: (
          <SvgIcon
            component={ListingIdSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '16px', sm: '26px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetPropertyAdExternalId
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
    ],
    land: [
      {
        icon: (
          <SvgIcon
            component={StructureFactorSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetBuildFactor propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={InOutPlanSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '20px', sm: '32px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetPlans propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={PricePerSquareMeterSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '19px', sm: '31px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetPricePerBuildableArea
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={UsageSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '16px', sm: '28px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetUsage propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={WithBuildingSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '19px', sm: '31px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetWithBuilding propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={CompensationSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '17px', sm: '29px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetCompensation propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={DimensionsSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '18px', sm: '30px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetDimensions propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={CoverageFactorSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '18px', sm: '30px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetCoverageFactor propertyAd={propertyAd} isExpanded={isExpanded} />
        ),
      },
      {
        icon: (
          <SvgIcon
            component={AccessSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '18px', sm: '30px' },
              height: 'auto',
            }}
          />
        ),
        getter: <GetAccess propertyAd={propertyAd} isExpanded={isExpanded} />,
      },
      {
        icon: (
          <SvgIcon
            component={ListingIdSVG}
            inheritViewBox
            sx={{
              color: 'black',
              width: { xs: '16px', sm: '26px' },
              height: 'auto',
            }}
          />
        ),
        getter: (
          <GetPropertyAdExternalId
            propertyAd={propertyAd}
            isExpanded={isExpanded}
          />
        ),
      },
    ],
  }

  const PROPERTY_TYPE = React.useMemo(() => {
    return [1, 4].includes(propertyAd.adCategory.id)
      ? 'estate'
      : [2, 5].includes(propertyAd.adCategory.id)
      ? 'commercial'
      : 'land'
  }, [propertyAd.adCategory.id])

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'grid',

          gridTemplateColumns: `repeat(5,  1fr)`,
          justifyContent: {
            xs: 'space-evenly',
            sm: isExpanded ? 'space-between' : 'flex-start',
            md: 'flex-start',
            lg: 'space-evenly',
          },
          gap: {
            xs: '10px',
            sm: !isExpanded ? '40px' : '20px 35px',
            md: '20px 60px',
            lg: '10px',
          },

          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: `repeat(5,  60px)`,
            justifyContent: !isExpanded ? 'flex-start' : 'space-between',
          },
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: `repeat(5,  79px)`,
          },
          [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: `repeat(5,  1fr)`,
          },
        }}
      >
        {icons[PROPERTY_TYPE]?.filter((_item, index) =>
          isExpanded
            ? true
            : matchesTablet
            ? isExpanded
              ? true
              : index <= 4
            : matchesSM
            ? isExpanded
              ? true
              : false
            : index <= 4
        ).map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',

              flexFlow: 'column wrap',
              rowGap: '7px',
              justifyContent: { xs: 'space-evenly' },
              alignItems: 'center',
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                height: { xs: '34px', sm: '56px' },
                width: { xs: '34px', sm: '56px' },
              }}
            >
              {item.icon}
            </Avatar>
            <Typography
              component="span"
              display="block"
              fontWeight={400}
              fontSize={{ xs: '14px', md: '16px' }}
              textAlign={'center'}
              sx={{ lineHeight: { xs: '16px', md: '19px' } }}
            >
              {item?.getter}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  )
}

export default ListingIcons
